import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import type {
  LayoutType,
  RelationshipsNode,
  RelationshipsViewProperties,
  RelationshipsViewSettings,
  Zoom,
} from './types';
import { initializeZoom } from './zoom';
import { createLayout, type RelationshipsPopover } from './initialiseLayout';
import { PopoverAtPoint, PlainTextPopover } from '@ardoq/popovers';
import { noop, throttle, debounce } from 'lodash';
import { ZoomTransform, select } from 'd3';
import { useResizeObserver } from '@ardoq/hooks';
import { xScaler } from '@ardoq/graph';
import { ViewCanvas } from 'tabview/relationshipDiagrams/atoms';
import { DisabledZoomControls } from '@ardoq/view-settings';
import { RelationshipsViewZoomControls } from './view/RelationshipsViewZoomControls';
import { RelationshipsViewDecorationsSvg } from './RelationshipsViewDecorationsSvg';
import { registerOutline } from './outline/outlineRegistry';
import { toOutline } from './outline/toOutline';
import {
  getFitNodeTransform,
  getNaturalZoomControlsDisabledState,
  zoomScaleExtent,
  transformViewCanvas,
} from './util';
import * as canvasHitTestRegistry from '../../canvasRendering/canvasHitTestRegistry';
import { BUTTON_CLICK_ZOOM_FACTOR } from './consts';
import { clearGraphSelection } from '@ardoq/component-selection';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { t } from '@ardoq/i18n';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { startViewWork } from 'tabview/activeViewsStatus/viewStatusReporter';

const i18nKeys = {
  canvasAriaLabel: 'relationsD3View:hierarchical.canvasAriaLabel',
} as const;

type HandleZoomChangedArgs = {
  transform: ZoomTransform;
  rootNode: RelationshipsNode;
  canvas: HTMLCanvasElement;
  layout: ReturnType<typeof createLayout> | null;
  setZoomControlsDisabledState: React.Dispatch<
    React.SetStateAction<DisabledZoomControls>
  >;
  isOptimizeLayoutDisabled: boolean;
};

const handleZoomChanged = debounce(
  ({
    transform,
    rootNode,
    canvas,
    layout,
    setZoomControlsDisabledState,
    isOptimizeLayoutDisabled,
  }: HandleZoomChangedArgs) => {
    const rootNodeFitTransform = getFitNodeTransform(canvas, rootNode);
    const currentZoomScaleExtent = zoomScaleExtent(
      {
        width: canvas.offsetWidth,
        height: canvas.offsetHeight,
      },
      rootNodeFitTransform.k
    );
    const currentTransform = layout?.getViewTransform();

    const isFitWindowAvailable =
      !currentTransform ||
      rootNodeFitTransform.k !== currentTransform.k ||
      rootNodeFitTransform.x !== currentTransform.x ||
      rootNodeFitTransform.y !== currentTransform.y;

    const isFitWindowDisabled =
      !isFitWindowAvailable && isOptimizeLayoutDisabled;

    const currentDisabledState =
      getNaturalZoomControlsDisabledState(transform.k, currentZoomScaleExtent) |
      (Number(isFitWindowDisabled) && DisabledZoomControls.FIT_WINDOW);
    setZoomControlsDisabledState(currentDisabledState);
  },
  40
);

const closestTo = (target: number, ...values: number[]) =>
  values.reduce((result, current) => {
    const resultDistance = Math.abs(target - result);
    const currentDistance = Math.abs(target - current);
    return currentDistance < resultDistance ? current : result;
  });

const scaleLocation = (
  dimension: number,
  previousDimension: number,
  location: number
) => xScaler([0, dimension], [0, previousDimension])(location);

const DEFAULT_FIT_DURATION = 500;

type FitNodeArgs = {
  canvas: HTMLCanvasElement;
  node: RelationshipsNode;
  zoom: Zoom;
  viewInstanceId: string;
  duration: number;
  viewStatusReporter: ViewStatusReporter;
};

const fitNode = ({
  canvas,
  node,
  zoom,
  viewInstanceId,
  duration,
  viewStatusReporter,
}: FitNodeArgs): void => {
  const xform = getFitNodeTransform(canvas, node);
  if (!duration) {
    transformViewCanvas(xform, viewInstanceId);
    return;
  }
  canvas.setAttribute('aria-busy', 'true');
  const fitToken = startViewWork(viewStatusReporter, 'animation');
  const reportFitFinished = () =>
    viewStatusReporter.reportWorkFinished(fitToken);
  requestAnimationFrame(() =>
    select(canvas)
      .transition()
      .duration(duration)
      .call(zoom.transform, xform)
      .on('end', () => {
        canvas.setAttribute('aria-busy', 'false');
        reportFitFinished();
      })
      .on('interrupt cancel', reportFitFinished)
  );
};

const RelationshipsViewCanvas = styled(ViewCanvas)`
  transition-property: opacity;
  transition-timing-function: ease-in;
`;

export type RelationshipsViewCoreProps = Omit<
  RelationshipsViewProperties,
  | 'viewSettings'
  | 'traversedIncomingReferenceTypes'
  | 'traversedOutgoingReferenceTypes'
  | 'referenceTypes'
  | 'componentTypes'
  | 'referenceModelTypes'
  | 'groups'
  | 'hasClones'
  | 'errors'
  | 'uiPropsAndCallbacks'
  | 'errorInfoBoxLabels'
> & {
  viewSettings: RelationshipsViewSettings;
  onLayoutComplete?: (needsFit: boolean) => void;
};

type FitNodeOptions = {
  node?: RelationshipsNode;
  duration?: number;
};

export type RelationshipsViewCoreRef = {
  fitNode: (options: FitNodeOptions) => void;
  setBranchOpen: (node: RelationshipsNode, open: boolean) => void;
  getZoom: () => Zoom | null;
  getLayout: () => ReturnType<typeof createLayout> | null;
  getCanvas: () => HTMLCanvasElement | null;
};

const RelationshipsViewCore = forwardRef<
  RelationshipsViewCoreRef,
  RelationshipsViewCoreProps
>(
  (
    {
      rootNode,
      links,
      viewSettings,
      viewInstanceId,
      disconnectedChildren,
      noConnectedComponents: _noConnectedComponents,
      isViewpointMode,
      isViewEmpty,
      i18nLocale,
      visualStates,
      relationshipsViewCommands,
      viewStatusReporter,
      onLayoutComplete = noop,
    }: RelationshipsViewCoreProps,
    ref
  ) => {
    const transformRef = useRef<ZoomTransform | undefined>(undefined);
    const zoom = useRef<Zoom | null>(null);
    const [zoomControlsDisabledState, setZoomControlsDisabledState] = useState(
      DisabledZoomControls.NONE
    );
    const [popover, setPopover] = useState<RelationshipsPopover | null>(null);
    const [isOptimizeLayoutDisabled, setIsOptimizeLayoutDisabled] =
      useState(false);
    const onLayoutBegin = useCallback(
      (layout: LayoutType) =>
        setIsOptimizeLayoutDisabled(layout === 'packLayout'),
      []
    );
    const layout = useRef<ReturnType<typeof createLayout> | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const previousCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const hasCanvasChanged = canvasRef.current !== previousCanvasRef.current;

    const viewSettingsRef = useRef<RelationshipsViewSettings>(viewSettings);
    viewSettingsRef.current = viewSettings;

    // Expose API to parent component
    useImperativeHandle(
      ref,
      () => ({
        fitNode: ({
          node,
          duration = DEFAULT_FIT_DURATION,
        }: FitNodeOptions) => {
          const nodeToFit = node || rootNode;
          if (!canvasRef.current || !zoom.current || !nodeToFit) {
            return;
          }
          fitNode({
            canvas: canvasRef.current,
            node: nodeToFit,
            zoom: zoom.current,
            viewInstanceId,
            duration,
            viewStatusReporter,
          });
          if (!duration) {
            layout.current?.redrawImmediate();
          }
        },
        setBranchOpen: (node: RelationshipsNode, open: boolean) => {
          layout.current?.setBranchOpen(node, open);
        },
        getZoom: () => zoom.current,
        getLayout: () => layout.current,
        getCanvas: () => canvasRef.current,
      }),
      [rootNode, viewInstanceId, viewStatusReporter]
    );

    useEffect(() => {
      if (rootNode && canvasRef.current && transformRef.current) {
        handleZoomChanged({
          transform: transformRef.current,
          rootNode,
          canvas: canvasRef.current,
          layout: layout.current,
          setZoomControlsDisabledState,
          isOptimizeLayoutDisabled,
        });
      }
    }, [isOptimizeLayoutDisabled, rootNode]);

    useEffect(() => {
      layout.current?.setViewSettings(viewSettings);
    }, [viewSettings]);

    useEffect(() => {
      if (!rootNode) return;
      return registerOutline(viewInstanceId, () =>
        toOutline({ rootNode, links })
      );
    }, [viewInstanceId, rootNode, links]);

    useEffect(() => {
      const sub = subscribeToAction(clearGraphSelection, () => {
        layout.current?.clearSelection();
      });
      return () => sub.unsubscribe();
    }, []);

    useEffect(() => {
      if (!canvasRef.current || !rootNode) {
        onLayoutComplete(false);
        return;
      }

      const layoutArgs = {
        canvas: canvasRef.current,
        rootNode,
        links,
        viewSettings: viewSettingsRef.current,
        viewInstanceId,
        isViewpointMode,
        setPopover,
        onLayoutBegin,
        commands: relationshipsViewCommands,
        viewStatusReporter,
      };

      if (!layout.current) {
        layout.current = createLayout(layoutArgs);
      } else {
        layout.current.initialize(layoutArgs);
      }
      const currentLayout = layout.current;

      const theZoom =
        zoom.current && !hasCanvasChanged
          ? zoom.current
          : initializeZoom(canvasRef.current, viewInstanceId);

      if (theZoom !== zoom.current) {
        zoom.current = theZoom;
      }
      // No `locate`: this view cannot say where one of its nodes is, so anything
      // anchored to one stays where it last was rather than being wrong.
      canvasHitTestRegistry.register(viewInstanceId, {
        hitTest: e => currentLayout.findNode(e)?.modelId ?? null,
      });

      return () => {
        canvasHitTestRegistry.unregister(viewInstanceId);
      };
    }, [
      links,
      rootNode,
      viewInstanceId,
      isViewpointMode,
      hasCanvasChanged,
      onLayoutComplete,
      setPopover,
      onLayoutBegin,
      relationshipsViewCommands,
      viewStatusReporter,
    ]);

    // Must follow the layout-init effect: enrichment closes over freshly regenerated visual links.
    useEffect(() => {
      layout.current?.setVisualStates(visualStates);
    }, [visualStates]);

    useEffect(() => {
      return () => {
        layout.current?.cleanup();
        layout.current = null;
        zoom.current?.on('zoom', null);
        zoom.current = null;
      };
    }, []);

    const previousWidth = useRef(canvasRef.current?.offsetWidth);
    const previousHeight = useRef(canvasRef.current?.offsetHeight);

    useResizeObserver(canvasRef, () => {
      const width = canvasRef.current?.offsetWidth;
      const height = canvasRef.current?.offsetHeight;
      if (
        previousWidth.current === undefined ||
        previousHeight.current === undefined ||
        (width === previousWidth.current &&
          height === previousHeight.current) ||
        !layout.current ||
        !zoom.current ||
        !canvasRef.current
      ) {
        previousWidth.current = width;
        previousHeight.current = height;
        return;
      }

      if (previousWidth.current === 0 || previousHeight.current === 0) {
        if (rootNode) {
          requestAnimationFrame(() => {
            if (canvasRef.current && zoom.current) {
              fitNode({
                canvas: canvasRef.current,
                node: rootNode,
                zoom: zoom.current,
                viewInstanceId,
                duration: 0,
                viewStatusReporter,
              });
            }
          });
        }
        previousWidth.current = width;
        previousHeight.current = height;
        return;
      }

      const viewTransform = layout.current.getViewTransform();
      const previousCanvasWidth = previousWidth.current * devicePixelRatio;
      const previousCanvasHeight = previousHeight.current * devicePixelRatio;
      const canvasWidth = (width ?? 0) * devicePixelRatio;
      const canvasHeight = (height ?? 0) * devicePixelRatio;

      const transformX = scaleLocation(
        canvasWidth,
        previousCanvasWidth,
        viewTransform.x
      );
      const transformY = scaleLocation(
        canvasHeight,
        previousCanvasHeight,
        viewTransform.y
      );

      const widthChange = canvasWidth / previousCanvasWidth;
      const heightChange = canvasHeight / previousCanvasHeight;
      const irregularResize = widthChange >= 1 !== heightChange >= 1;
      const sizeChange = irregularResize
        ? 1
        : closestTo(1, widthChange, heightChange);
      const scale = viewTransform.k * sizeChange;
      zoom.current.transform(
        select(canvasRef.current),
        new ZoomTransform(
          scale / devicePixelRatio,
          transformX / devicePixelRatio,
          transformY / devicePixelRatio
        )
      );
      previousWidth.current = width;
      previousHeight.current = height;
    });

    const onMouseMove = useCallback(
      (event: React.MouseEvent) => {
        if (!layout.current) {
          return undefined;
        }

        layout.current.mouseMove(event, viewSettings);
      },
      [viewSettings]
    );
    const throttledMouseMove = throttle(onMouseMove, 100, {
      leading: false,
      trailing: true,
    });

    const onClick = useCallback(
      (event: React.MouseEvent) => {
        if (!layout.current) {
          return undefined;
        }

        layout.current.click(event, viewSettings);
      },
      [viewSettings]
    );

    const onDoubleClick = useCallback(
      (event: React.MouseEvent) => {
        if (!layout.current) {
          return undefined;
        }

        layout.current.doubleClick(event, viewSettings);
      },
      [viewSettings]
    );

    useEffect(() => {
      if (isViewEmpty && layout.current) {
        layout.current.unsetLayout();
        zoom.current = null;
      }
      onLayoutComplete(true);
    }, [isViewEmpty, onLayoutComplete]);

    const zoomIn = () =>
      select(canvasRef.current!)
        .transition()
        .duration(500)
        .call(zoom.current!.scaleBy, BUTTON_CLICK_ZOOM_FACTOR);

    const zoomOut = () =>
      select(canvasRef.current!)
        .transition()
        .duration(500)
        .call(zoom.current!.scaleBy, BUTTON_CLICK_ZOOM_FACTOR ** -1);
    const onOptimizeLayout = layout.current?.optimizeLayout ?? noop;

    const onZoomToFit = useCallback(() => {
      if (!rootNode) {
        return;
      }
      fitNode({
        canvas: canvasRef.current!,
        node: rootNode,
        zoom: zoom.current!,
        viewInstanceId,
        duration: DEFAULT_FIT_DURATION,
        viewStatusReporter,
      });
    }, [rootNode, viewInstanceId, viewStatusReporter]);

    if (isViewEmpty) {
      return null;
    }

    return (
      <>
        {!isViewpointMode && (
          <RelationshipsViewZoomControls
            disabledState={zoomControlsDisabledState}
            onZoomFit={onZoomToFit}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            onOptimizeLayout={onOptimizeLayout}
            viewSettings={viewSettings}
            isOptimizeLayoutDisabled={isOptimizeLayoutDisabled}
            i18nLocale={i18nLocale}
          />
        )}
        <RelationshipsViewCanvas
          ref={canvasRef}
          aria-label={t(
            i18nLocale,
            i18nKeys.canvasAriaLabel,
            'Relationships view'
          )}
          aria-busy={false}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onMouseMove={throttledMouseMove}
          onMouseOut={() => {
            throttledMouseMove?.cancel();
            layout.current?.mouseOut();
          }}
          onContextMenu={event => layout.current?.contextMenu(event)}
        />
        <RelationshipsViewDecorationsSvg
          viewInstanceId={viewInstanceId}
          disconnectedChildren={disconnectedChildren}
          isViewpointMode={isViewpointMode}
          viewSettings={viewSettings}
        />
        {popover && (
          <PopoverAtPoint
            point={popover.point}
            isOpen
            onClose={() => setPopover(null)}
            placement={{ side: 'bottom', overflow: 'center' }}
          >
            <PlainTextPopover>{popover.text}</PlainTextPopover>
          </PopoverAtPoint>
        )}
      </>
    );
  }
);

export { RelationshipsViewCore };
